import { rgba } from 'polished'
import styled from 'styled-components'

export const PagedGrid = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  gap: 12px;
`

export const GridFrame = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
`

export const GridViewport = styled.div`
  position: relative;
  height: 100%;
`

export const GridScrollArea = styled.div`
  height: 100%;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  /* touch-first paging: the grid always comes to rest on a whole row */
  scroll-snap-type: y mandatory;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

export const GridEdgeFade = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 14px;
  pointer-events: none;
  opacity: ${({ $visible }) => $visible ? 1 : 0};
  transition: opacity 0.15s;
  ${({ $edge }) => $edge === 'top'
    ? 'top: 0; background: linear-gradient(to bottom, var(--color-body), transparent);'
    : 'bottom: 0; background: linear-gradient(to top, var(--color-body), transparent);'}
`

export const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(176px, 1fr));
  gap: 12px;
`

export const Pager = styled.div`
  flex: 0 0 64px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: space-between;
  gap: 8px;
`

export const PageButton = styled.button`
  flex: 1 1 0;
  max-height: 160px;
  min-height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: var(--color-light);
  border: 1px solid var(--color-line);
  border-radius: var(--radius-control);
  color: ${({ theme }) => theme.colors.global};
  cursor: pointer;
  touch-action: manipulation;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.1s ease-out, background-color 0.1s, border-color 0.1s;

  svg {
    width: 28px;
    height: 28px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2.4;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &:not(:disabled):active {
    transform: scale(0.96);
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => rgba(theme.colors.primary, 0.08)};
    color: ${({ theme }) => theme.colors.primaryText || theme.colors.primary};
  }

  &:disabled {
    cursor: default;
    color: var(--color-muted);
    opacity: 0.35;
    border-color: transparent;
    background: transparent;
  }
`

export const PageIndicator = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;

  strong {
    font-size: 1.25rem;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.global};
  }

  span {
    font-size: 0.8125rem;
    color: var(--color-muted);
  }
`

export const GridItem = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 8px 10px 10px;
  background: #ffffff;
  border: 1px solid var(--color-line);
  border-radius: var(--radius-card);
  /* Only the first card of each page is a snap point: swipes rest on whole pages */
  scroll-snap-align: ${({ $pageStart }) => ($pageStart ? 'start' : 'none')};
  scroll-snap-stop: ${({ $pageStart }) => ($pageStart ? 'always' : 'normal')};
  cursor: pointer;
  touch-action: manipulation;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.1s ease-out, border-color 0.1s, background-color 0.1s;

  &:active {
    transform: scale(0.97);
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => rgba(theme.colors.primary, 0.06)};
  }
`

const media = `
  height: 68px;
  margin-bottom: 8px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`

export const GridItemImage = styled.div`
  ${media}
  background: #F6F7F9;

  img {
    max-width: 100%;
    max-height: 100%;
    padding: 8px;
    object-fit: contain;
    mix-blend-mode: multiply;
    pointer-events: none;
    -webkit-user-drag: none;
  }
`

export const GridItemIcon = styled.div`
  ${media}
  background: #F6F7F9;
  font-size: 3rem;
  line-height: 1;
`

export const GridItemFooter = styled.div`
  margin-top: auto;
  padding-top: 4px;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`

export const GridItemCode = styled.div`
  font-size: 0.8125rem;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.04em;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.muted};
`

export const GridItemTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 500;
  line-height: 1.3;
  /* always reserve three lines so prices line up and pack sizes stay visible */
  min-height: calc(0.9rem * 1.3 * 3);
  color: ${({ theme }) => theme.colors.global};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
`

export const GridItemPrice = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.global};
`

export const GridEmpty = styled.div`
  grid-column: 1 / -1;
  padding: 48px 16px;
  text-align: center;
  color: ${({ theme }) => theme.colors.muted};
  border: 1px dashed #D5DAE0;
  border-radius: 14px;
`
